import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import {
  ListEnd,
  Trash2,
  CheckCheck,
  Users,
  Calendar,
  BookText,
  MessageSquare,
} from 'lucide-react';
import { showToast } from '@/components/Toast';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import MobileHeader from '@/components/MobileHeader';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

type SharedUser = {
  id: number | string;
  name: string;
  email: string;
};

type SharedConversationRow = {
  id: number;
  conversation: {
    conversationId: string;
    title: string;
    project?: {
      id: string;
      name: string;
      role: string | null;
    } | null;
  };
  sharedAt: string;
  sharedFrom: SharedUser;
  sharedDescription?: string;
  lastVisitedAt?: string | null;
};

// 新增：我的分享记录类型
type MyShareLog = {
  id: number;
  sharedBy: SharedUser;
  sharedDescription: string;
  conversations: Array<{
    conversationId: string;
    projectId?: string;
    project?: {
      id: string;
      name: string;
      role: string;
    };
    title: string;
    createdAt: string;
  }>;
  sharedTo: SharedUser[];
  sharedAt: string;
};

interface SharedPageProps {
  onOpenSharedConversation: (sharedConversationId: number) => void;
  onOpenConversation: (conversationId: string) => void;
  onRefreshConversations: () => void | Promise<void>;
}

export default function SharedPage({
  onOpenSharedConversation,
  onOpenConversation: _onOpenConversation,
  onRefreshConversations,
}: SharedPageProps) {
  const { t } = useTranslation();
  useForceUpdate();
  void _onOpenConversation;

  // Tab 状态
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');

  // 我接收到的分享 - 原有逻辑
  const [items, setItems] = useState<SharedConversationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  // 我的分享记录 - 新增逻辑
  const [myShareLogs, setMyShareLogs] = useState<MyShareLog[]>([]);
  const [isLoadingMyShares, setIsLoadingMyShares] = useState(false);
  const [mySharesPage, setMySharesPage] = useState(1);
  const [mySharesHasMore, setMySharesHasMore] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const hasLoadedMySharesRef = useRef(false);

  const [activateTarget, setActivateTarget] =
    useState<SharedConversationRow | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<SharedConversationRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  // 加载我接收到的分享 - 原有逻辑
  const loadShared = useCallback(async (force = false) => {
    if (hasLoadedRef.current && !force) {
      return;
    }
    hasLoadedRef.current = true;
    setIsLoading(true);
    try {
      const res = await apiGet<any>('/api/ai/conversations/shared');
      const data = res?.data || res || {};
      const raw: unknown[] = Array.isArray(data.sharedConversations)
        ? data.sharedConversations
        : [];

      const mapped: SharedConversationRow[] = raw
        .map((it: any): SharedConversationRow | null => {
          const conv = it?.conversation || {};

          const conversationId = String(conv?.conversationId ?? '').trim();
          if (!conversationId) return null;

          return {
            id: Number(it?.id ?? 0),
            conversation: {
              conversationId,
              title: String(conv?.title ?? 'Untitled'),
              project: it?.conversation?.project ?? null,
            },
            sharedFrom: it?.sharedFrom || {},
            sharedAt: String(it?.sharedAt ?? ''),
            sharedDescription: String(it?.sharedDescription ?? ''),
            lastVisitedAt: it?.lastVisitedAt ?? null,
          };
        })
        .filter((it): it is SharedConversationRow => it !== null);

      setItems(mapped);
      hasLoadedRef.current = true;
    } catch {
      // Handle error silently
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 加载我的分享记录 - 新增逻辑
  const loadMyShares = useCallback(
    async (page = 1, reset = false) => {
      if (isLoadingMyShares) return;

      if (reset) {
        hasLoadedMySharesRef.current = false;
        setMySharesPage(1);
      }

      if (hasLoadedMySharesRef.current && !reset && page === 1) {
        return;
      }

      setIsLoadingMyShares(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: '20',
        });

        const res = await apiGet<any>(
          `/api/ai/conversations/shares?${params.toString()}`
        );
        const data = res?.data || res || {};
        const logs: unknown[] = Array.isArray(data.shareLogs)
          ? data.shareLogs
          : [];

        const mapped: MyShareLog[] = logs
          .map((it: any): MyShareLog | null => {
            if (!it) return null;

            return {
              id: Number(it?.id ?? 0),
              sharedBy: it?.sharedBy || {},
              sharedDescription: String(it?.sharedDescription ?? ''),
              conversations: Array.isArray(it?.conversations)
                ? it.conversations
                : [],
              sharedTo: Array.isArray(it?.sharedTo) ? it.sharedTo : [],
              sharedAt: String(it?.sharedAt ?? ''),
            };
          })
          .filter((it): it is MyShareLog => it !== null);

        if (reset || page === 1) {
          setMyShareLogs(mapped);
        } else {
          setMyShareLogs(prev => [...prev, ...mapped]);
        }

        setMySharesHasMore(mapped.length === 20);
        hasLoadedMySharesRef.current = true;
      } catch {
        // Handle error silently
      } finally {
        setIsLoadingMyShares(false);
      }
    },
    [isLoadingMyShares]
  );

  useEffect(() => {
    if (activeTab === 'received') {
      loadShared();
    } else {
      loadMyShares();
    }
  }, [activeTab, loadShared, loadMyShares]);

  const emptyState = useMemo(() => {
    if (activeTab === 'received') {
      if (isLoading) return t('common.loading') || 'Loading...';
      return t('sidebar.noSharedConversations') || 'No shared conversations';
    } else {
      if (isLoadingMyShares) return t('common.loading') || 'Loading...';
      return t('share.noShareLogs') || 'No share history logs';
    }
  }, [isLoading, isLoadingMyShares, activeTab, t]);

  const formatSharedBy = (row: SharedConversationRow) => {
    const by = row.sharedFrom;
    if (!by) return '-';
    return `${by.name} (${by.email})`;
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value || '-';
    return date.toLocaleString();
  };

  const formatSharedToList = (users: SharedUser[]) => {
    if (!users || users.length === 0) return '-';
    return users.map(u => `${u.name} (${u.email})`).join(', ');
  };

  const handleActivate = useCallback(async () => {
    if (!activateTarget) return;
    setIsActivating(true);
    try {
      const res = await apiPost<any>(
        `/api/ai/conversations/shared/${activateTarget.id}/initialization`,
        {}
      );
      const data = res?.data || res || {};
      if (data.code && data.code !== 200) {
        throw new Error(data.message || 'Convert failed');
      }

      setItems(prev => prev.filter(it => it.id !== activateTarget.id));
      await onRefreshConversations();
      window.refreshSidebarSharedCount?.();
      showToast(
        t('toast.convertedSuccessfully') || 'Converted successfully',
        'success'
      );
    } catch (e) {
      showToast(
        e instanceof Error
          ? e.message
          : t('errors.failedToConvertShared') ||
              'Failed to convert shared conversation',
        'error'
      );
    } finally {
      setIsActivating(false);
      setActivateTarget(null);
    }
  }, [activateTarget, onRefreshConversations, t]);

  const unvisitedCount = useMemo(() => {
    return items.filter(item => !item.lastVisitedAt).length;
  }, [items]);

  const handleMarkAllRead = useCallback(async () => {
    if (unvisitedCount === 0) return;
    setIsMarkingAllRead(true);
    try {
      await apiPost('/api/ai/conversations/shared/visit', {});
      hasLoadedRef.current = false;
      await loadShared(true);
      window.refreshSidebarSharedCount?.();
      showToast(t('toast.allMarkedAsRead') || 'All marked as read', 'success');
    } catch (err) {
      showToast(
        t('toast.failedToMarkAllAsRead') || 'Failed to mark all as read',
        'error'
      );
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [unvisitedCount, loadShared, t]);

  return (
    <div className="mx-auto mt-0 sm:mt-4 mb-14 w-full h-full min-h-0 px-4 md:px-8 lg:mt-6 max-w-4xl flex flex-col">
      {/* Mobile Header */}
      <MobileHeader className="-mx-4 md:-mx-8">
        <h3 className="text-base font-bold">{t('sidebar.shared')}</h3>
      </MobileHeader>
      {/* Tab 切换器 - 固定高度，避免切换时抖动 */}
      <div className="flex-shrink-0 px-2 py-4 border-b border-gray-200 lg:px-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('received')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'received'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('share.receivedShares') || 'Received Shares'}
            {unvisitedCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {unvisitedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'sent'
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('share.sentShares') || 'Sent Shares'}
          </button>
        </div>
      </div>

      {/* Tab 内容 */}
      <div className="flex-1 min-h-0 flex flex-col">
        {activeTab === 'received' ? (
          // Tab 1: 我接收到的分享
          <>
            {/* 标题栏 */}
            <div className="flex-shrink-0 px-6 pl-0 ml-6 py-2 border-b border-gray-200 flex items-center justify-between">
              {/* <h2 className="text-lg font-medium text-gray-900">
                {t('share.receivedShares') || '我接收到的分享'}
              </h2> */}
              <button
                onClick={handleMarkAllRead}
                disabled={unvisitedCount === 0 || isMarkingAllRead}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  unvisitedCount === 0 || isMarkingAllRead
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={
                  unvisitedCount === 0
                    ? t('tooltips.noUnreadItems') || 'No unread items'
                    : `${t('tooltips.markAsRead') || 'Mark as read'} (${unvisitedCount})`
                }
              >
                <CheckCheck size={16} />
                <span>
                  {isMarkingAllRead
                    ? t('common.marking') || 'Marking...'
                    : t('tooltips.markAllAsRead') || 'Mark all as read'}
                </span>
              </button>
            </div>

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">{emptyState}</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {items.map(row => (
                    <div
                      key={row.id}
                      className="px-6 pl-0 ml-6 py-4 hover:bg-gray-50 cursor-pointer flex items-start justify-between gap-4"
                      onClick={() => onOpenSharedConversation(row.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-normal mb-1 truncate flex items-center gap-2"
                          title={row.conversation.title}
                        >
                          {!row.lastVisitedAt && (
                            <span
                              className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full"
                              title={t('common.new') || 'New'}
                            />
                          )}
                          <span className="truncate">
                            {row.conversation.title}
                          </span>
                          {row?.conversation?.project && (
                            <span
                              className="flex-shrink-0 flex items-center px-1 sm:px-2.5 py-1 text-xs border rounded-sm transition-colors text-gray-600 bg-blue-50 hover:bg-blue-100 border-blue-200 overflow-hidden"
                              style={{ maxWidth: '16rem' }}
                              title={`${row.conversation.project.role || ''}${row.conversation.project.role ? ' | ' : ''}${row.conversation.project.name}`}
                            >
                              <span
                                className={`flex-shrink-0 pr-1 font-bold ${
                                  row.conversation.project.role === 'owner'
                                    ? 'text-purple-700'
                                    : row.conversation.project.role ===
                                        'manager'
                                      ? 'text-blue-700'
                                      : row.conversation.project.role ===
                                          'editor'
                                        ? 'text-green-700'
                                        : 'text-gray-600'
                                }`}
                              >
                                {row.conversation.project.role
                                  ? '[' + row.conversation.project.role + ']'
                                  : ''}
                              </span>
                              <span className="truncate">
                                {row.conversation.project.name}
                              </span>
                            </span>
                          )}
                        </div>
                        <div
                          className="text-sm text-gray-700 break-words line-clamp-3"
                          title={
                            row.sharedDescription?.trim()
                              ? row.sharedDescription
                              : t('share.noDescription') || 'No description'
                          }
                        >
                          {row.sharedDescription?.trim()
                            ? row.sharedDescription
                            : t('share.noDescription') || 'No description'}
                        </div>
                        <div
                          className="mt-2 text-xs text-gray-500 truncate"
                          title={formatSharedBy(row)}
                        >
                          {formatSharedBy(row)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        <div className="flex items-center gap-0 flex-shrink-0">
                          <Button
                            className={`px-1 bg-transparent hover:bg-transparent ${
                              row.conversation.project &&
                              (row.conversation.project.role === null ||
                                row.conversation.project.role === 'viewer')
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-900 hover:text-gray-600'
                            }`}
                            size="sm"
                            disabled={
                              !!(
                                row.conversation.project &&
                                (row.conversation.project.role === null ||
                                  row.conversation.project.role === 'viewer')
                              )
                            }
                            onClick={e => {
                              e.stopPropagation();
                              setActivateTarget(row);
                            }}
                            title={
                              row.conversation.project &&
                              (row.conversation.project.role === null ||
                                row.conversation.project.role === 'viewer')
                                ? t(
                                    'share.cannotConvertInsufficientPermission'
                                  ) || 'Insufficient permission, cannot convert'
                                : t('share.convertToNormalConversation') ||
                                  'Convert to normal conversation'
                            }
                            aria-label={t('share.convert') || 'Convert'}
                          >
                            <ListEnd size={16} />
                          </Button>
                          <Button
                            className="px-2 bg-transparent text-red-500 hover:text-red-400  hover:bg-transparent"
                            size="sm"
                            onClick={e => {
                              e.stopPropagation();
                              setDeleteTarget(row);
                            }}
                            title={
                              t('tooltips.deleteSharedConversation') ||
                              'Delete shared conversation'
                            }
                            aria-label={t('common.delete') || 'Delete'}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(row.sharedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          // Tab 2: 我的分享操作记录
          <>
            {/* 标题栏 */}
            {/* <div className="flex-shrink-0 px-6 pl-0 ml-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {t('share.sentShares') || '我的分享操作记录'}
              </h2>
            </div> */}

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto">
              {myShareLogs.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">{emptyState}</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {myShareLogs.map(log => (
                    <div
                      key={log.id}
                      className="px-6 pl-0 ml-6 py-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-4">
                        <div className="min-w-0 flex-1">
                          {/* 分享描述 */}
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <BookText size={14} className="flex-shrink-0" />
                            <span className="font-bold">
                              {t('share.shareDescription') ||
                                'Share Description'}
                              :
                            </span>
                            <span
                              className="truncate flex-1"
                              title={
                                log.sharedDescription ||
                                t('share.noDescription') ||
                                'No description'
                              }
                            >
                              {log.sharedDescription ||
                                t('share.noDescription') ||
                                'No description'}
                            </span>
                          </div>

                          {/* 分享给谁 */}
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <Users size={14} className="flex-shrink-0" />
                            <span className="font-bold">
                              {t('share.sharedTo') || 'Shared To'}:
                            </span>
                            <span
                              className="truncate flex-1"
                              title={formatSharedToList(log.sharedTo)}
                            >
                              {formatSharedToList(log.sharedTo)}
                            </span>
                          </div>

                          {/* 分享时间 */}
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar size={14} className="flex-shrink-0" />
                            <span className="font-bold">
                              {t('share.sharedAtTime') || 'Shared At Time'}:
                            </span>
                            <span className="flex-1">
                              {formatDate(log.sharedAt)}
                            </span>
                          </div>

                          {/* 分享的会话列表 */}
                          {log.conversations &&
                            log.conversations.length > 0 && (
                              <div>
                                <div className="flex gap-2 text-sm text-gray-600 font-bold">
                                  <MessageSquare
                                    size={14}
                                    className="flex-shrink-0"
                                  />
                                  {t('share.sharedConversations') ||
                                    'Shared Conversations'}{' '}
                                  ({log.conversations.length})
                                </div>
                                <div className="space-y-1 pl-3 border-l-2 border-gray-200">
                                  {(expandedLogs.has(log.id)
                                    ? log.conversations
                                    : log.conversations.slice(0, 3)
                                  ).map((conv, idx) => (
                                    <div
                                      key={idx}
                                      className="text-sm text-gray-700 truncate"
                                      title={
                                        conv.title +
                                        (conv.project
                                          ? ` (${conv.project.name})`
                                          : '')
                                      }
                                    >
                                      • {conv.title}
                                      {conv.project && (
                                        <span className="ml-2 text-xs text-gray-500">
                                          ({conv.project.name})
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                  {log.conversations.length > 3 && (
                                    <button
                                      className="text-xs text-blue-500 hover:text-blue-700 italic cursor-pointer mt-0.5"
                                      onClick={() =>
                                        setExpandedLogs(prev => {
                                          const next = new Set(prev);
                                          if (next.has(log.id)) {
                                            next.delete(log.id);
                                          } else {
                                            next.add(log.id);
                                          }
                                          return next;
                                        })
                                      }
                                    >
                                      {expandedLogs.has(log.id)
                                        ? t('share.collapseConversations') ||
                                          'Collapse'
                                        : t('share.moreConversations', {
                                            count: log.conversations.length - 3,
                                          })}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* 加载更多 */}
                  {mySharesHasMore && (
                    <div className="px-6 py-4 text-center">
                      <button
                        onClick={() => {
                          const nextPage = mySharesPage + 1;
                          setMySharesPage(nextPage);
                          loadMyShares(nextPage);
                        }}
                        disabled={isLoadingMyShares}
                        className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                      >
                        {isLoadingMyShares
                          ? t('common.loading') || 'Loading...'
                          : t('common.loadMore') || 'Load More'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={!!activateTarget}
        title={t('confirmDialog.convertConversation') || 'Convert Conversation'}
        message={
          t('share.convertConversationMessage') ||
          'Are you sure you want to convert this conversation?'
        }
        confirmText={t('share.convert') || 'Convert'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="warning"
        isLoading={isActivating}
        onCancel={() => {
          if (isActivating) return;
          setActivateTarget(null);
        }}
        onConfirm={handleActivate}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={
          t('share.deleteSharedConversation') || 'Delete Shared Conversation'
        }
        message={
          t('share.deleteSharedConversationMessage') ||
          'Are you sure you want to delete this shared conversation?'
        }
        confirmText={t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
        onCancel={() => {
          if (isDeleting) return;
          setDeleteTarget(null);
        }}
        onConfirm={async () => {
          if (!deleteTarget) return;
          setIsDeleting(true);
          try {
            await apiDelete(`/api/ai/conversations/shared/${deleteTarget.id}`);
            setItems(prev => prev.filter(it => it.id !== deleteTarget.id));
            await onRefreshConversations();
            window.refreshSidebarSharedCount?.();
            showToast(
              t('toast.deletedSuccessfully') || 'Delete successful',
              'success'
            );
          } catch (e) {
            showToast(
              e instanceof Error
                ? e.message
                : t('errors.failedToDeleteShared') || 'Delete failed',
              'error'
            );
          } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}
